import React from "react"
import {useTranslation} from "react-i18next"
import {BuildingOffice2Icon, RocketLaunchIcon} from "@heroicons/react/24/outline"

import KlaraAIImage from "../../../assets/KlaraAI.png"
import {Badge} from "../../../components/Badge.tsx"
import {ProfileImage} from "../../../components/ProfileImage.tsx"
import {getCurrentLanguage} from "../../../i18n.ts"
import {useUserSettingsOrLogout} from "../../../queries/user.ts"
import {
  ASalesAssistantDealStatuses,
  ASalesAssistantSalesPersonSummary,
  ASalesAssistantUpdateCenterEntry,
  ASalesAssistantUpdateCenterEntryTypeValues,
} from "../../../services/types.generated.ts"
import {apiDateToJS} from "../../../utils/dateArithmetics.ts"
import {enumTranslKey} from "../../../utils/i18n.tsx"
import {formatSeconds} from "../../../utils/index.ts"
import {isSaaSCompanyUser} from "../../../utils/types.ts"
import {StepCard} from "./UpdateCenterStep.tsx"

const UpdateCenterEntryDate: React.FC<{trackedAt: string}> = ({trackedAt}) => {
  return (
    <div className={"mb-4 flex items-center justify-between self-stretch py-2"}>
      <div className={"flex min-w-0 flex-1 flex-col items-start justify-start gap-2 py-2"}>
        <div className={"h-px w-full bg-indigo-200"}></div>
      </div>
      <div
        className={
          "flex items-center justify-center gap-2 rounded-[32px] px-4 py-1 outline outline-indigo-200 sm:px-8 md:px-20"
        }
      >
        <div className={"justify-start font-['Sora'] text-sm leading-5 font-normal whitespace-nowrap text-gray-900"}>
          {apiDateToJS(trackedAt).toLocaleString(getCurrentLanguage(), {
            dateStyle: "medium",
          })}
        </div>
      </div>
      <div className={"flex min-w-0 flex-1 flex-col items-start justify-start gap-2 py-2"}>
        <div className={"h-px w-full bg-indigo-200"}></div>
      </div>
    </div>
  )
}

const UpdateCenterEntryHeader: React.FC<{
  isCrmSystem: boolean
  isSaaSCompany: boolean
  salesPerson?: ASalesAssistantSalesPersonSummary | null
  totalActivities: number
  totalDeals: number
  trackedAt: string
  voiceProgressTracker?: ASalesAssistantUpdateCenterEntry["voice_progress_tracker"]
}> = ({isCrmSystem, isSaaSCompany, salesPerson, totalActivities, totalDeals, trackedAt, voiceProgressTracker}) => {
  const {t} = useTranslation()

  const salesPersonName = [salesPerson?.first_name, salesPerson?.last_name].filter(Boolean).join(" ")
  const systemName = isCrmSystem ? t("SalesAssistant_UpdateCenter_CrmSystem") : t("SalesAssistant_UpdateCenter_KlaraAI")

  return (
    <div className={"mb-4 flex items-start justify-start gap-2 self-stretch"}>
      {isSaaSCompany ? (
        <ProfileImage
          className={"size-10 shrink-0"}
          src={salesPerson?.profile_picture_thumbnail_url ?? undefined}
          alt={salesPersonName}
          title={salesPersonName}
        />
      ) : (
        !isCrmSystem && (
          <img
            className={"shrink-0"}
            src={KlaraAIImage}
            alt={t("SalesAssistant_UpdateCenter_KlaraAI")}
            width={40}
            height={40}
          />
        )
      )}
      <div className={"flex min-w-0 flex-1 flex-col items-start justify-start gap-4"}>
        <div className={"flex flex-col items-start justify-start gap-1 self-stretch"}>
          <div className={"flex items-center justify-start gap-1"}>
            <div className={"justify-start font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>
              {isSaaSCompany
                ? t("SalesAssistant_UpdateCenter_SalesPersonTitle", {
                    name: salesPersonName,
                    totalActivities,
                    totalDeals,
                    activity: t("SalesAssistant_UpdateCenter_Activity", {count: totalActivities}),
                    deal: t("SalesAssistant_UpdateCenter_Deal", {count: totalDeals}),
                  })
                : isCrmSystem
                  ? t("SalesAssistant_UpdateCenter_CrmTitle", {
                      totalActivities,
                      totalDeals,
                      activity: t("SalesAssistant_UpdateCenter_Activity", {count: totalActivities}),
                      deal: t("SalesAssistant_UpdateCenter_Deal", {count: totalDeals}),
                    })
                  : t("SalesAssistant_UpdateCenter_KlaraTitle", {
                      totalActivities,
                      totalDeals,
                      activity: t("SalesAssistant_UpdateCenter_Activity", {count: totalActivities}),
                      deal: t("SalesAssistant_UpdateCenter_Deal", {count: totalDeals}),
                    })}
            </div>
          </div>
          <div className={"flex items-center justify-start gap-1 self-stretch"}>
            {isSaaSCompany && (
              <>
                <div className={"justify-start font-['Sora'] text-xs font-normal text-zinc-500"}>{systemName}</div>
                <div className={"size-[3px] rounded-full bg-zinc-500"} />
              </>
            )}
            <div className={"justify-start font-['Sora'] text-xs font-normal text-zinc-500"}>
              {apiDateToJS(trackedAt).toLocaleString(getCurrentLanguage(), {
                month: "long",
                year: "numeric",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
            {voiceProgressTracker && (
              <>
                <div className={"size-[3px] rounded-full bg-zinc-500"} />
                <div className={"justify-start font-['Sora'] text-xs font-normal text-zinc-500"}>
                  {t("SalesAssistant_UpdateCenter_CallDuration")} {formatSeconds(voiceProgressTracker?.call_duration)}
                </div>
                <div className={"size-[3px] rounded-full bg-zinc-500"} />
                <div className={"justify-start font-['Sora'] text-xs font-normal text-zinc-500"}>
                  {t("SalesAssistant_UpdateCenter_CallType")}{" "}
                  {voiceProgressTracker.call_type &&
                    t(enumTranslKey("SalesAssistantUpdateCenterCallType", voiceProgressTracker.call_type))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export const UpdateCenterEntryCard: React.FC<{
  entry: ASalesAssistantUpdateCenterEntry
  showDateHeader?: boolean
}> = ({entry, showDateHeader = true}) => {
  const {t} = useTranslation()
  const {user} = useUserSettingsOrLogout()
  const totalActivities =
    entry.update_center_events?.reduce((sum, e) => sum + (e.update_center_steps?.length ?? 0), 0) ?? 0
  const totalDeals = entry.update_center_events?.length ?? 0
  const isCrmSystem = entry.entry_type === ASalesAssistantUpdateCenterEntryTypeValues.CrmSystem
  const isSaaSCompany = isSaaSCompanyUser(user)

  return (
    <>
      {showDateHeader && <UpdateCenterEntryDate trackedAt={entry.tracked_at} />}
      <div className={"mb-8 rounded-2xl border border-indigo-100 p-6"}>
        <UpdateCenterEntryHeader
          isCrmSystem={isCrmSystem}
          isSaaSCompany={isSaaSCompany}
          salesPerson={entry.sales_person}
          totalActivities={totalActivities}
          totalDeals={totalDeals}
          trackedAt={entry.tracked_at}
          voiceProgressTracker={entry.voice_progress_tracker}
        />
        {entry.update_center_events?.map((event, eventIndex) => {
          return (
            <div
              key={event.id ?? eventIndex}
              className={"mb-4 flex flex-col items-start justify-start gap-10 self-stretch"}
            >
              <div className={"flex flex-col items-start justify-center self-stretch"}>
                <div className={"flex w-full flex-nowrap items-start justify-start gap-2.5"}>
                  <div className={"flex size-9 shrink-0 items-center justify-center rounded-full bg-indigo-500"}>
                    <BuildingOffice2Icon className={"size-5 text-white"} />
                  </div>
                  <div className={"flex min-w-0 flex-1 flex-col items-start justify-start overflow-hidden"}>
                    <div className={"flex w-full min-w-0 flex-nowrap items-center justify-start gap-2.5"}>
                      <div
                        className={
                          "min-w-0 overflow-hidden font-['Sora'] text-sm leading-5 font-semibold text-ellipsis whitespace-nowrap text-gray-900"
                        }
                      >
                        {event?.deal?.title}
                      </div>
                      {event.deal.status === ASalesAssistantDealStatuses.Won && (
                        <Badge color={"blue"} className={"flex shrink-0 items-center gap-1 py-1"}>
                          <RocketLaunchIcon className={"h-3 w-3"} />
                          <span className={"font-['Sora'] text-xs font-normal text-white"}>
                            {t("SalesAssistant_UpdateCenter_Won")}
                          </span>
                        </Badge>
                      )}
                      {event.deal.status === ASalesAssistantDealStatuses.Lost && (
                        <Badge color={"custom"} className={"shrink-0 bg-red-600 py-1"}>
                          <span className={"font-['Sora'] text-xs font-normal text-white"}>
                            {t("SalesAssistant_UpdateCenter_Lost")}
                          </span>
                        </Badge>
                      )}
                    </div>
                    {event.deal.selling_title && (
                      <div className={"justify-start font-['Sora'] text-xs font-normal text-zinc-500"}>
                        {event.deal.selling_title}
                      </div>
                    )}
                  </div>
                </div>
                {event.update_center_steps?.map((step, stepIndex) => (
                  <StepCard key={step.id ?? stepIndex} step={step} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
